import { Injectable, inject } from '@angular/core';
import { CanDeactivateFn, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';

type LeaveHandler = (next: RouterStateSnapshot) => Observable<boolean> | boolean;

/**
 * Where the wizard shell answers "may this open child form be left?".
 *
 * The child-form routes (`…/new`, `…/edit/:rowId`) are componentless, so `canDeactivate` is
 * handed no component to ask — and the one that knows the answer, the shell, provides the wizard
 * services at its own level, out of reach of a route guard's injector. The shell registers itself
 * here for as long as it is mounted instead.
 */
@Injectable({ providedIn: 'root' })
export class ChildFormLeaveGate {
  private handler: LeaveHandler | null = null;

  /** Register the shell's answer. Returns the unregister call, for the shell's teardown. */
  register(handler: LeaveHandler): () => void {
    this.handler = handler;
    return () => {
      if (this.handler === handler) {
        this.handler = null;
      }
    };
  }

  confirm(next: RouterStateSnapshot): Observable<boolean> | boolean {
    return this.handler ? this.handler(next) : true;
  }
}

/**
 * Leaving an open coupon / promotion / catalog form by the router — browser Back or Forward, a
 * link — rather than the header's back arrow, which already asks before it discards.
 *
 * Both are the same gesture (ADR 0001 D8: Back discards a child form), so both ask the same
 * question, through the same dialog, only when there is something unsaved to lose.
 */
export const childFormLeaveGuard: CanDeactivateFn<unknown> = (_component, _route, _state, next) =>
  inject(ChildFormLeaveGate).confirm(next);
